import React, { useState, useEffect } from "react";
import { FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, FaTimes, FaSave, FaKey, FaCheck, FaServer, FaCalendarAlt } from "react-icons/fa";

const GeminiKeys = () => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [editingKey, setEditingKey] = useState(null);
  const [newKey, setNewKey] = useState({
    name: "",
    key: "",
    isActive: true
  });

  // Fetch all keys
  useEffect(() => {
    const fetchKeys = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://octoberfour-60f2c682a1d1.herokuapp.com/api/gemini-keys');
        const result = await response.json();
        
        if (result.success) {
          setKeys(result.data);
        } else {
          throw new Error(result.message || "Failed to fetch keys");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchKeys();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewKey(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  // Handle edit form input changes
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingKey(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  // Add a new key
  const handleAddKey = async (e) => {
    e.preventDefault();
    
    if (!newKey.name.trim() || !newKey.key.trim()) {
      setMessage({ text: "Name and key are required", type: "error" });
      return;
    }
    
    try {
      const response = await fetch('https://octoberfour-60f2c682a1d1.herokuapp.com/api/gemini-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newKey)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setKeys(prev => [result.data, ...prev]);
        setNewKey({ name: "", key: "", isActive: true });
        setMessage({ text: "API key added successfully!", type: "success" });
      } else {
        throw new Error(result.message || "Failed to add key");
      }
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // Update an existing key
  const handleUpdateKey = async (e) => {
    e.preventDefault();
    
    if (!editingKey.name.trim() || !editingKey.key.trim()) {
      setMessage({ text: "Name and key are required", type: "error" });
      return;
    }
    
    try {
      const response = await fetch(`https://octoberfour-60f2c682a1d1.herokuapp.com/api/gemini-keys/${editingKey._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingKey)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setKeys(prev => 
          prev.map(key => key._id === editingKey._id ? result.data : key)
        );
        setEditingKey(null);
        setMessage({ text: "API key updated successfully!", type: "success" });
      } else {
        throw new Error(result.message || "Failed to update key");
      }
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // Delete a key
  const handleDeleteKey = async (id) => {
    if (!window.confirm("Are you sure you want to delete this API key?")) {
      return;
    }
    
    try {
      const response = await fetch(`https://octoberfour-60f2c682a1d1.herokuapp.com/api/gemini-keys/${id}`, {
        method: 'DELETE'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setKeys(prev => prev.filter(key => key._id !== id));
        setMessage({ text: "API key deleted successfully!", type: "success" });
      } else {
        throw new Error(result.message || "Failed to delete key");
      }
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // Toggle key active status
  const handleToggleStatus = async (id) => {
    try {
      const response = await fetch(`https://octoberfour-60f2c682a1d1.herokuapp.com/api/gemini-keys/${id}/toggle`, {
        method: 'PATCH'
      });
      
      const result = await response.json();
      
      if (result.success) {
        setKeys(prev => 
          prev.map(key => key._id === id ? result.data : key)
        );
        setMessage({ 
          text: `API key ${result.data.isActive ? "activated" : "deactivated"} successfully!`, 
          type: "success" 
        });
      } else {
        throw new Error(result.message || "Failed to toggle key status");
      }
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: "error" });
    }
  };

  // Start editing a key
  const startEditing = (key) => {
    setEditingKey({ ...key });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingKey(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger d-flex align-items-center" role="alert">
        <FaTimes className="me-2" />
        <div>Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-5" style={{ backgroundColor: '#f8fafc' }}>
      <div className="row justify-content-center">
        <div className="col-xxl-10">
          {/* Header Section */}
          <div className="d-flex justify-content-between align-items-center mb-5 pb-3 border-bottom">
            <div>
              <h1 className="display-6 fw-bold mb-1" style={{ color: '#1e293b' }}>
                <FaKey className="me-3 text-primary" />
                Gemini API Keys Management
              </h1>
              <p className="text-muted mb-0">Manage your API keys for accessing Gemini services</p>
            </div>
            <div className="d-flex align-items-center">
              <span className="badge bg-primary rounded-pill px-3 py-2 fs-6">
                {keys.length} {keys.length === 1 ? 'Key' : 'Keys'}
              </span>
            </div>
          </div>
          
          {/* Notification Alert */}
          {message.text && (
            <div className={`alert ${message.type === "success" ? "alert-success" : "alert-danger"} alert-dismissible fade show mb-4`} role="alert">
              <div className="d-flex align-items-center">
                {message.type === "success" ? <FaCheck className="me-2" /> : <FaTimes className="me-2" />}
                <div>{message.text}</div>
              </div>
              <button type="button" className="btn-close" onClick={() => setMessage({ text: "", type: "" })}></button>
            </div>
          )}
          
          <div className="row g-4">
            {/* Add New Key Form */}
            <div className="col-lg-5">
              <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                <div className="card-header bg-white py-3 border-0" style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <h5 className="card-title mb-0 d-flex align-items-center">
                    <FaPlus className="me-2 text-primary" />
                    Add New API Key
                  </h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleAddKey}>
                    <div className="mb-4">
                      <label htmlFor="keyName" className="form-label fw-medium">Key Name</label>
                      <input
                        type="text"
                        className="form-control form-control-lg"
                        id="keyName"
                        name="name"
                        value={newKey.name}
                        onChange={handleInputChange}
                        placeholder="e.g., Production Key"
                        style={{ borderRadius: '8px' }}
                        required
                      />
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="apiKey" className="form-label fw-medium">API Key</label>
                      <input
                        type="password"
                        className="form-control form-control-lg"
                        id="apiKey"
                        name="key"
                        value={newKey.key}
                        onChange={handleInputChange}
                        placeholder="Enter your Gemini API key"
                        style={{ borderRadius: '8px' }}
                        required
                      />
                    </div>
                    
                    <div className="mb-4 form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isActive"
                        name="isActive"
                        checked={newKey.isActive}
                        onChange={handleInputChange}
                        style={{ fontSize: '1.5rem' }}
                      />
                      <label className="form-check-label fw-medium" htmlFor="isActive">
                        Active Status
                      </label>
                    </div>
                    
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg w-100 d-flex align-items-center justify-content-center"
                      style={{ borderRadius: '8px', backgroundColor: '#3b82f6' }}
                    >
                      <FaPlus className="me-2" />
                      Add Key
                    </button>
                  </form>
                </div>
              </div>
            </div>
            
            {/* Edit Key Form */}
            {editingKey && (
              <div className="col-lg-5">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
                  <div className="card-header bg-white py-3 border-0" style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <h5 className="card-title mb-0 d-flex align-items-center">
                      <FaEdit className="me-2 text-warning" />
                      Edit API Key
                    </h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleUpdateKey}>
                      <div className="mb-4">
                        <label htmlFor="editKeyName" className="form-label fw-medium">Key Name</label>
                        <input
                          type="text"
                          className="form-control form-control-lg"
                          id="editKeyName"
                          name="name"
                          value={editingKey.name}
                          onChange={handleEditInputChange}
                          style={{ borderRadius: '8px' }}
                          required
                        />
                      </div>
                      
                      <div className="mb-4">
                        <label htmlFor="editApiKey" className="form-label fw-medium">API Key</label>
                        <input
                          type="password"
                          className="form-control form-control-lg"
                          id="editApiKey"
                          name="key"
                          value={editingKey.key}
                          onChange={handleEditInputChange}
                          style={{ borderRadius: '8px' }}
                          required
                        />
                      </div>
                      
                      <div className="mb-4 form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="editIsActive"
                          name="isActive"
                          checked={editingKey.isActive}
                          onChange={handleEditInputChange}
                          style={{ fontSize: '1.5rem' }}
                        />
                        <label className="form-check-label fw-medium" htmlFor="editIsActive">
                          Active Status
                        </label>
                      </div>
                      
                      <div className="d-flex gap-3">
                        <button
                          type="submit"
                          className="btn btn-warning flex-fill d-flex align-items-center justify-content-center"
                          style={{ borderRadius: '8px' }}
                        >
                          <FaSave className="me-2" />
                          Update Key
                        </button>
                        <button
                          type="button"
                          onClick={cancelEditing}
                          className="btn btn-outline-secondary flex-fill d-flex align-items-center justify-content-center"
                          style={{ borderRadius: '8px' }}
                        >
                          <FaTimes className="me-2" />
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Keys List */}
          <div className="mt-5">
            <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
              <div className="card-header bg-white py-3 border-0" style={{ borderBottom: '1px solid #e2e8f0' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0 d-flex align-items-center">
                    <FaServer className="me-2 text-primary" />
                    Your API Keys
                  </h5>
                  <span className="text-muted small">Total: {keys.length} keys</span>
                </div>
              </div>
              <div className="card-body p-0">
                {keys.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-4 text-muted">
                      <FaKey style={{ fontSize: '4rem' }} />
                    </div>
                    <h5 className="text-muted mb-3">No API keys found</h5>
                    <p className="text-muted mb-0">Add your first key using the form above</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th scope="col" className="ps-4 fw-medium" style={{ color: '#64748b' }}>Name</th>
                          <th scope="col" className="fw-medium" style={{ color: '#64748b' }}>Key</th>
                          <th scope="col" className="fw-medium" style={{ color: '#64748b' }}>Status</th>
                          <th scope="col" className="fw-medium" style={{ color: '#64748b' }}>Created</th>
                          <th scope="col" className="text-end pe-4 fw-medium" style={{ color: '#64748b' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {keys.map((key) => (
                          <tr key={key._id}>
                            <td className="ps-4">
                              <div className="fw-medium" style={{ color: '#334155' }}>{key.name}</div>
                            </td>
                            <td>
                              <code className="bg-light px-3 py-2 rounded" style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                {key.key.substring(0, 8)}...{key.key.substring(key.key.length - 4)}
                              </code>
                            </td>
                            <td>
                              <span className={`badge ${key.isActive ? 'bg-success' : 'bg-danger'} px-3 py-2`}>
                                {key.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="text-muted">
                              <div className="d-flex align-items-center">
                                <FaCalendarAlt className="me-2" />
                                {formatDate(key.createdAt)}
                              </div>
                            </td>
                            <td className="text-end pe-4">
                              <div className="btn-group" role="group">
                                <button
                                  onClick={() => handleToggleStatus(key._id)}
                                  className={`btn btn-sm ${key.isActive ? 'btn-warning' : 'btn-success'}`}
                                  title={key.isActive ? "Disable" : "Enable"}
                                  style={{ borderRadius: '6px 0 0 6px' }}
                                >
                                  {key.isActive ? <FaToggleOff /> : <FaToggleOn />}
                                </button>
                                <button
                                  onClick={() => startEditing(key)}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Edit"
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  onClick={() => handleDeleteKey(key._id)}
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete"
                                  style={{ borderRadius: '0 6px 6px 0' }}
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeminiKeys;